import urlcat from 'urlcat';
import { formatIdentityValue } from '@newfront-insurance/account-common';
import { AuthRole } from '@newfront-insurance/auth-api';
import { toGroupedRecord } from '@newfront-insurance/core';
import { hasAnyScope } from '@newfront-insurance/next-auth';
import { IdentityType } from '@newfront-insurance/account-api';

function route(path) {
  return _ref => {
    let {
      params,
      basePath
    } = _ref;
    return `${basePath || ''}${urlcat(path, params || {})}`;
  };
}

function makeRoute(path) {
  return {
    path,
    getUrl: route(path)
  };
}

const routes = {
  getAccountByUuid: makeRoute('/account/:accountUuid'),
  getAccountManagers: makeRoute(`/account-managers`),
  getOperationsEmployees: makeRoute('/operations-employees'),
  getProducers: makeRoute(`/producers`),
  searchEngine: makeRoute('/search-engine')
};

const getStructuredDataTextField = (result, fieldName) => {
  var _field$textValues, _field$textValues2;

  const {
    metadata
  } = result;
  if (!(metadata !== null && metadata !== void 0 && metadata.fields)) return undefined;
  const field = metadata.fields.find(f => f.name === fieldName);
  return field !== null && field !== void 0 && (_field$textValues = field.textValues) !== null && _field$textValues !== void 0 && _field$textValues.values && (field === null || field === void 0 ? void 0 : (_field$textValues2 = field.textValues) === null || _field$textValues2 === void 0 ? void 0 : _field$textValues2.values.length) > 0 ? field.textValues.values[0] : undefined;
};
const getStructuredDataArrayField = (result, fieldName) => {
  var _field$textValues3;

  const {
    metadata
  } = result;
  if (!(metadata !== null && metadata !== void 0 && metadata.fields)) return undefined;
  const field = metadata.fields.find(f => f.name === fieldName);
  return (field === null || field === void 0 ? void 0 : (_field$textValues3 = field.textValues) === null || _field$textValues3 === void 0 ? void 0 : _field$textValues3.values) || [];
};
const IDENTITY_TYPE_LABELS = {
  [IdentityType.BUSINESS_IDENTIFIER]: 'Business identifier',
  [IdentityType.DRIVERS_LICENSE]: "Driver's license",
  [IdentityType.PASSPORT]: 'Passport',
  [IdentityType.PERMANENT_RESIDENT_CARD]: 'Permanent resident card',
  [IdentityType.STATE_ID_CARD]: 'State ID card',
  [IdentityType.SSN]: 'Social Security Number'
};
function getBusinessIdentifierLabel(countryCode) {
  return countryCode.toLocaleLowerCase() === 'us' ? 'FEIN' : 'Business identifier';
}

const hasAnyAccountViewAccessScopes = [AuthRole.AM_MANAGER, AuthRole.ENGINEER, AuthRole.OPERATIONS, AuthRole.PRODUCER_MANAGER, AuthRole.SALES_MANAGER, // TODO(ACC-743): Remove AMs and CSA from this list once they can be added to an account's servicing teams
AuthRole.ACCOUNT_MANAGER, AuthRole.CSA];
function formatAccountResults(result, authDetails) {
  const {
    title,
    url
  } = result;
  const dba = getStructuredDataTextField(result, 'doingBusinessAs') || '';
  const accountUuid = getStructuredDataTextField(result, 'uuid') || '';
  const relatedUsers = getStructuredDataArrayField(result, 'relatedUsers') || [];
  const hasViewAccess = hasAnyScope(authDetails.roles, hasAnyAccountViewAccessScopes) || relatedUsers.includes(authDetails.user.uuid);
  return {
    id: accountUuid,
    type: 'account',
    label: title || '',
    description: dba,
    data: result,
    hasViewAccess,
    routing: {
      absolute: url || '',
      relative: {
        pathname: '/accounts/[accountUuid]',
        query: {
          accountUuid
        }
      }
    }
  };
}
function formatAccount(account, accountEmployeeRoleMappings) {
  const {
    identities,
    type,
    mailingAddress
  } = account;
  const definitionListItems = [];

  if (identities.length > 0) {
    definitionListItems.push(...formatIdentitiesDefinitions(identities, type));
  }

  definitionListItems.push({
    title: 'Mailing address',
    type: 'location',
    value: mailingAddress ? `${mailingAddress.street}, ${mailingAddress.city}` : '-',
    subValue: mailingAddress ? `${mailingAddress.state}, ${mailingAddress.zip}` : undefined
  });
  return { ...account,
    definitionListItems,
    employeeRoleMappings: groupAccountEmployeeRoleMappingsByRoleId(accountEmployeeRoleMappings)
  };
}
function formatPublicAccount(account, accountEmployeeRoleMappings) {
  const {
    identities,
    type
  } = account;
  const definitionListItems = [];

  if (identities.length > 0) {
    definitionListItems.push(...formatIdentitiesDefinitions(identities, type));
  }

  return { ...account,
    definitionListItems,
    employeeRoleMappings: groupAccountEmployeeRoleMappingsByRoleId(accountEmployeeRoleMappings)
  };
}

function formatIdentitiesDefinitions(identities, accountType) {
  const identitiesDefinitions = identities.map(identity => {
    return {
      title: accountType === 'commercial' ? getBusinessIdentifierLabel(identity.countryCode) : IDENTITY_TYPE_LABELS[identity.type],
      type: 'number',
      value: identity.value ? formatIdentityValue(identity) : '-'
    };
  });
  return identitiesDefinitions;
}

function groupAccountEmployeeRoleMappingsByRoleId(apiAccountEmployeeRoleMappings) {
  const accountEmployeeRoleMappings = apiAccountEmployeeRoleMappings.map(_ref => {
    let {
      accountUuid,
      roleId,
      userUuid
    } = _ref;
    return {
      accountUuid,
      roleId,
      userUuid
    };
  });
  return toGroupedRecord(accountEmployeeRoleMappings, accountRoleMapping => accountRoleMapping.roleId);
}

export { IDENTITY_TYPE_LABELS as I, getStructuredDataArrayField as a, formatPublicAccount as b, formatAccount as c, getBusinessIdentifierLabel as d, formatAccountResults as f, getStructuredDataTextField as g, hasAnyAccountViewAccessScopes as h, routes as r };
